import { iniciales } from '@/components/rdt/rdt-domain';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';

interface Props {
  nombre: string;
  /** URL versionada de la foto (UsuarioMini.fotoUrl) o null (autor histórico). */
  fotoUrl: string | null;
  size?: number;
  className?: string;
  /** Qué ayuda muestra el avatar al pasar el mouse (Avivador v2, T3a; §13:
   *  nunca el `title` nativo en el mosaico):
   *  - string: `Tooltip` de la app con ese texto («Ana Vera — Control RDT»).
   *  - null: sin tooltip (el nombre ya está a la vista, p. ej. el autor del
   *    comentario de la card).
   *  - undefined (default): como siempre, tooltip con `nombre`.
   *  La API no cambia (la usa el Avivador). */
  tooltip?: string | null;
  /** Convierte el `<span>` del `render` del `TooltipTrigger` en una parada de
   *  Tab (`tabIndex={0}` + `aria-label` con el texto del tooltip). Default
   *  `false`: Base UI NO le inyecta `tabIndex` a un `render` que no es
   *  nativamente interactivo (`TooltipTrigger`/`useRenderElement`, confirmado
   *  contra el código de `@base-ui/react` — hallazgo ALTA de la revisión
   *  home-comportamiento, R4a), así que sin esto el avatar entero queda
   *  inalcanzable por teclado. Se pide `true` SOLO donde hace falta (decisión
   *  del orquestador 26/9: el avatar de Control RDT de la card de la home,
   *  `obra-card.tsx`, es una parada de Tab, como antes de la migración) — el
   *  resto de los consumidores de este componente no cambia. */
  enfocable?: boolean;
}

// Avatar de persona — R3-C (plan_migracion_reui.md): `Avatar`/`AvatarImage`/
// `AvatarFallback` de ui/ (c-avatar-2,
// https://reui.io/preview/base/components/c-avatar-2?ref=mcp), tal cual. El
// fallback a iniciales y el fix del 404 de Safari (Avivador v2, T3a: el error
// de una foto ya pedida podía llegar antes de que React enganchara el
// `onError`) los resuelve el propio `AvatarImage` de Base UI: crea su Image()
// con `onerror` ANTES de asignar `src` y, además, chequea `image.complete`
// como fast path para imágenes cacheadas — el mismo problema que el parche a
// mano de antes cubría, así que ese parche se cae acá.
export function AvatarPersona({ nombre, fotoUrl, size = 22, className, tooltip, enfocable = false }: Props) {
  const dimension = `${size}px`;

  const avatar = (
    <Avatar className={className} style={{ width: dimension, height: dimension }}>
      {fotoUrl && <AvatarImage src={fotoUrl} alt="" />}
      <AvatarFallback style={{ fontSize: Math.max(8, Math.round(size * 0.36)) }}>{iniciales(nombre)}</AvatarFallback>
    </Avatar>
  );

  if (tooltip === null) return avatar;

  const textoTooltip = tooltip ?? nombre;

  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <span
            className="inline-flex shrink-0"
            tabIndex={enfocable ? 0 : undefined}
            aria-label={enfocable ? textoTooltip : undefined}
          />
        }
      >
        {avatar}
      </TooltipTrigger>
      <TooltipContent>{textoTooltip}</TooltipContent>
    </Tooltip>
  );
}
